<template>
  <v-container fluid>
    <v-dialog
      v-model="login"
      persistent
      scrollable
      max-width="480">
      <v-card>
        <v-toolbar
          color="secondary"
          >
          <v-toolbar-title>
            {{translate('login')}}
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text
          class="mt-3">
          <v-container>
            <v-text-field
              v-model="username"
              :label="translate('Username')"
              @keypress.enter="loginUser"
              prepend-icon="mdi-account"
            ></v-text-field>
            <v-text-field
              type="password"
              v-model="password"
              :label="translate('login_password')"
              @keypress.enter="loginUser"
              prepend-icon="mdi-lock-check"
            ></v-text-field>
            <v-select
              class="dropdown-value"
              dense
              prepend-icon="mdi-rocket-launch"
              v-model="api_link"
              :items="environments"
              item-text="text"
              item-value="link"
            >
              <template v-slot:selection="{ item }">
                <v-icon small class="mr-2" :color="statusColor(item.link)">mdi-circle</v-icon>
                <span>{{ item.text }}</span>
              </template>
              <template v-slot:item="{ item, attrs, on }">
                <v-list-item v-bind="attrs" v-on="on">
                  <v-list-item-content>
                    <v-list-item-title class="d-flex align-center">
                      <v-icon small class="mr-2" :color="statusColor(item.link)">mdi-circle</v-icon>
                      <span>{{ item.text }}</span>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-select>
            <div class="text-caption d-flex align-center mt-2">
              <v-icon small class="mr-1" :color="statusColor(api_link)">mdi-server</v-icon>
              <span class="mr-1">API:</span>
              <strong class="mr-2">{{ statusLabel(api_link) }}</strong>
              <span v-if="statusFor(api_link).latencyMs !== undefined" class="mr-2">
                ({{ statusFor(api_link).latencyMs }}ms)
              </span>
              <v-spacer></v-spacer>
              <v-btn icon small :loading="apiStatusLoading" @click.stop="refreshApiStatuses([api_link])">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </div>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            large
            color="accent"
            @click.stop="loginUser();">
            {{translate('login')}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <Snackbar
        :text="notificationText"
        :type="notificationType"
        @close="closeNotification"/>
  </v-container>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';

import Snackbar from '../components/Snackbar';

const apiToken = process.env.VUE_APP_API_TOKEN;

export default {
  name: 'Login',
  data() {
    const browserHost = (window && window.location && window.location.hostname)
      ? window.location.hostname
      : '127.0.0.1';
    const localApiLink = `http://${browserHost}:5555`;

    return {
      username: '',
      password: '',
      api_link: process.env.VUE_APP_API_BASE_URL,
      login: true,
      notificationType: 'error',
      notificationText: '',
      apiStatusByLink: {},
      apiStatusTimeoutMs: 2500,
      apiStatusLoading: false,
      environments: [
        { text: 'BiH', link: 'https://api.zame.dev', entityCode: 'bh' },
        { text: 'Croatia', link: 'https://hr1.zame.dev', entityCode: 'hr' },
        { text: 'Macedonia', link: 'https://mk.zame.dev', entityCode: 'mk' },
        { text: 'Staging (MK kopija)', link: 'https://cg.zame.dev', entityCode: 'cg' },
        { text: 'Serbia', link: 'https://sr.zame.dev', entityCode: 'sr' },
        { text: 'Slovenia', link: 'https://sl.zame.dev', entityCode: 'sl' },
        { text: 'Srbija lokalno', link: 'http://10.200.114.75:5555', entityCode: 'sr' },
        { text: 'Local environment', link: localApiLink, entityCode: 'le' },
      ],
    };
  },
  components: {
    Snackbar,
  },
  computed: {
    ...mapGetters('translations', [
      'translate',
    ]),
  },
  created() {
    localStorage.setItem('entity_code', 'none');
    this.preselectEnvironmentFromRedirect();
    this.refreshApiStatuses();
  },
  watch: {
    api_link() {
      this.refreshApiStatuses([this.api_link]);
    },
  },
  methods: {
    // Ticket emails link to app.zame.dev/plannings/<id>?entity=<code>. When such
    // a link sends the user here, preselect the API of the entity it belongs to.
    preselectEnvironmentFromRedirect() {
      const redirectTarget = String(this.$route.query.redirect || '');
      if (!redirectTarget) {
        return;
      }

      const queryStart = redirectTarget.indexOf('?');
      if (queryStart === -1) {
        return;
      }

      const params = new URLSearchParams(redirectTarget.slice(queryStart + 1));
      const entityCode = String(params.get('entity') || '').toLowerCase();
      if (!entityCode) {
        return;
      }

      const environment = this.environments.find(item => item.entityCode === entityCode);
      if (environment) {
        this.api_link = environment.link;
      }
    },
    closeNotification() {
      this.notificationText = '';
      this.notificationType = '';
    },
    resolveApiLinkForBrowser(link) {
      if (!link) {
        return link;
      }

      try {
        const parsed = new URL(link);
        const browserHost = window.location.hostname;
        const isLoopbackTarget = parsed.hostname === '127.0.0.1' || parsed.hostname === 'localhost';

        if (isLoopbackTarget && browserHost) {
          parsed.hostname = browserHost;
          return parsed.toString().replace(/\/+$/, '');
        }
      } catch (error) {
        return link;
      }

      return link.replace(/\/+$/, '');
    },
    normalizeApiLink(link) {
      return (link || '').replace(/\/+$/, '');
    },
    statusFor(link) {
      const normalized = this.resolveApiLinkForBrowser(link);
      return this.apiStatusByLink[normalized] || { state: 'unknown' };
    },
    statusColor(link) {
      const { state } = this.statusFor(link);
      if (state === 'up') return 'success';
      if (state === 'degraded') return 'warning';
      if (state === 'down') return 'error';
      if (state === 'checking') return 'grey';
      return 'grey';
    },
    statusLabel(link) {
      const { state } = this.statusFor(link);
      if (state === 'up') return 'Up';
      if (state === 'degraded') return 'Degraded';
      if (state === 'down') return 'Down';
      if (state === 'checking') return 'Checking';
      return 'Unknown';
    },
    async checkApi(link) {
      const normalized = this.resolveApiLinkForBrowser(link);
      if (!normalized) return { state: 'unknown' };

      const startedAt = (window.performance && performance.now) ? performance.now() : Date.now();
      const checkedAt = new Date().toISOString();

      this.$set(this.apiStatusByLink, normalized, { state: 'checking', checkedAt });

      try {
        const res = await axios({
          method: 'GET',
          url: `${normalized}/health/`,
          timeout: this.apiStatusTimeoutMs,
          headers: apiToken ? { 'x-api-key': apiToken } : undefined,
        });

        const endedAt = (window.performance && performance.now) ? performance.now() : Date.now();
        const latencyMs = Math.round(endedAt - startedAt);
        const next = {
          state: 'up',
          httpStatus: res.status,
          latencyMs,
          checkedAt,
        };
        this.$set(this.apiStatusByLink, normalized, next);
        return next;
      } catch (err) {
        const endedAt = (window.performance && performance.now) ? performance.now() : Date.now();
        const latencyMs = Math.round(endedAt - startedAt);
        const httpStatus = (err && err.response && err.response.status) ? err.response.status : null;

        // Any HTTP response means the server is reachable (even if /health is missing).
        let state = 'down';
        if (httpStatus) {
          if (httpStatus >= 500) state = 'degraded';
          else state = 'up';
        }
        const next = {
          state,
          httpStatus,
          latencyMs,
          checkedAt,
        };
        this.$set(this.apiStatusByLink, normalized, next);
        return next;
      }
    },
    async refreshApiStatuses(links) {
      const toCheck = (links && links.length)
        ? links
        : this.environments.map(e => e.link);

      this.apiStatusLoading = true;
      try {
        await Promise.all(toCheck.map(link => this.checkApi(link)));
      } finally {
        this.apiStatusLoading = false;
      }
    },
    async loginUser() {
      const apiLink = this.resolveApiLinkForBrowser(this.api_link);
      const result = await this.$store.dispatch('auth/login', {
        username: this.username,
        password: this.password,
        apiLink,
      });
      if (result && result.success) {
        localStorage.setItem('api_link', apiLink);
        for (let i = 0; i < this.environments.length; i += 1) {
          if (this.environments[i].link === this.api_link) {
            localStorage.setItem('entity_code', this.environments[i].entityCode);
            break;
          }
        }
        const redirectTarget = this.$route.query.redirect && this.$route.query.redirect !== '/login'
          ? this.$route.query.redirect
          : '/';
        this.$router.replace(redirectTarget).catch(() => {});
      } else {
        this.notificationText = this.loginFailureMessage(result);
      }
    },
    loginFailureMessage(result) {
      if (result && result.reason === 'session') {
        return this.translate('login_session_problem');
      }

      if (result && result.reason === 'login_request') {
        return this.translate('login_api_unreachable');
      }

      return this.translate('login_wrong_password');
    },
  },
};
</script>

<style>
  .v-input__slot {
    padding: 6px;
  }
  .v-input__prepend-outer {
    padding-top: 6px;
  }
</style>
